import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, MenuItem, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, CircularProgress, InputAdornment, IconButton, Tooltip,
  TablePagination,
} from '@mui/material';
import { Search, Refresh, History } from '@mui/icons-material';
import { InvokeApi, apiList } from '../../services/ApiService';
import { Config } from '../../config/Config';
import dayjs from 'dayjs';
import { useAuth } from '../../context/AuthContext';

const ACTION_COLORS = {
  fine_add:         { bg: '#ffebee', color: '#c62828', label: 'Fine Added' },
  fine_delete:      { bg: '#f3e5f5', color: '#6a1b9a', label: 'Fine Deleted' },
  salary_edit:      { bg: '#e3f2fd', color: '#1565c0', label: 'Salary Edit' },
  attendance_edit:  { bg: '#e8f5e9', color: '#2e7d32', label: 'Attendance Edit' },
  time_reduce:      { bg: '#fff3e0', color: '#e65100', label: 'Time Reduced' },
  employee_edit:    { bg: '#e8eaf6', color: '#1a237e', label: 'Employee Edit' },
  bank_edit:        { bg: '#e0f2f1', color: '#00695c', label: 'Bank Edit' },
};

const getActionStyle = (type) => ACTION_COLORS[type] || { bg: '#f5f5f5', color: '#555', label: type };

export default function AuditLogs() {
  const { user } = useAuth();
  const isHR = user?.role === 'HR';
  const hrDept = user?.department || '';
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [actorFilter, setActorFilter] = useState('');
  const [fromDate, setFromDate] = useState(dayjs().subtract(7, 'day').format('YYYY-MM-DD'));
  const [toDate, setToDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const hrdepts = hrDept.split(',');
  const [dept, setDept] = useState([]);

  const load = async () => {
    setLoading(true);
    const data = await InvokeApi(Config.main_url + apiList.getAuditLogs, {
      from_date: fromDate,
      to_date: toDate,
      action_type: actionFilter || undefined,
      limit: 500,
    });
    let result = data?.logs || [];
      if (isHR && hrDept) {
        const empData = await InvokeApi(Config.main_url + apiList.getEmployeesByDepartment, { department: dept, status: 'Active' });
        const deptIds = new Set((empData?.employee || []).map(e => String(e.ID)));
        result = result.filter(r => deptIds.has(String(r.employee_id)));
      }
    setLogs(result);
    setLoading(false);
    setPage(0);
  };

  useEffect(() => { load(); }, []);

  const filtered = logs.filter(l => {
    const q = search.toLowerCase();
    const matchSearch = (
      l.employee_name?.toLowerCase().includes(q) ||
      l.done_by_name?.toLowerCase().includes(q) ||
      l.action_type?.toLowerCase().includes(q) ||
      String(l.employee_id).includes(q) ||
      l.note?.toLowerCase().includes(q)
    );
    const matchActor = !actorFilter || l.actor_type === actorFilter;
    return matchSearch && matchActor;
  });

  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1a237e">Audit Logs</Typography>
          <Typography variant="body2" color="text.secondary">{filtered.length} records found</Typography>
        </Box>
        <Tooltip title="Refresh">
          {isHR &&
            <TextField
              select
              size="small"
              label="Department"
              value={dept}
              onChange={(e) => setDept(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              {/* <MenuItem value="All">All Departments</MenuItem> */}

              {hrdepts.map((d, index) => (
                <MenuItem key={index} value={d}>
                  {d}
                </MenuItem>
              ))}
            </TextField>}
          <IconButton onClick={load} sx={{ border: '1px solid #e8eaf6', borderRadius: 2, color: '#1a237e' }}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', mb: 3 }}>
        <CardContent sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField size="small" placeholder="Search employee, user, action..." value={search}
            onChange={e => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 200 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
          <TextField select size="small" label="Action Type" value={actionFilter}
            onChange={e => setActionFilter(e.target.value)} sx={{ minWidth: 160 }}>
            <MenuItem value="">All Actions</MenuItem>
            {Object.entries(ACTION_COLORS).map(([k, v]) => (
              <MenuItem key={k} value={k}>{v.label}</MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label="Done By" value={actorFilter}
            onChange={e => setActorFilter(e.target.value)} sx={{ minWidth: 130 }}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
            <MenuItem value="employee">Employee</MenuItem>
          </TextField>
          <TextField size="small" label="From" type="date" value={fromDate}
            onChange={e => setFromDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 140 }} />
          <TextField size="small" label="To" type="date" value={toDate}
            onChange={e => setToDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 140 }} />
          <Button variant="contained" onClick={load}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
            Search
          </Button>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress sx={{ color: '#1a237e' }} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ background: '#f5f6fa' }}>
                    {['Time', 'Action', 'Employee', 'Value', 'Note', 'Done By'].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e', fontSize: 12, whiteSpace: 'nowrap' }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                        <History sx={{ fontSize: 48, color: '#e8eaf6', display: 'block', mx: 'auto', mb: 1 }} />
                        No audit logs found
                      </TableCell>
                    </TableRow>
                  ) : paginated.map((log) => {
                    const style = getActionStyle(log.action_type);
                    return (
                      <TableRow key={log.ID} hover>
                        <TableCell sx={{ fontSize: 11, whiteSpace: 'nowrap', color: 'text.secondary' }}>
                          {log.created_at ? dayjs(log.created_at).format('DD MMM YY') : '—'}
                        </TableCell>
                        <TableCell>
                          <Chip label={style.label} size="small"
                            sx={{ background: style.bg, color: style.color, fontWeight: 700, fontSize: 10 }} />
                        </TableCell>
                        <TableCell>
                          <Typography fontSize={12} fontWeight={600}>{log.employee_name || '—'}</Typography>
                          {log.employee_id && (
                            <Typography fontSize={10} color="text.secondary">#{log.employee_id}</Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ fontSize: 12, color: '#2e7d32', fontWeight: 600, maxWidth: 140, wordBreak: 'break-word' }}>
                          {log.new_value || '—'}
                        </TableCell>
                        <TableCell sx={{ fontSize: 11, maxWidth: 200, wordBreak: 'break-word', color: 'text.secondary' }}>
                          {log.note || '—'}
                        </TableCell>
                        <TableCell>
                          <Typography fontSize={12} fontWeight={700} color="#1a237e">
                            {log.done_by_name || '—'}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.3, flexWrap: 'wrap' }}>
                            <Chip
                              label={log.actor_type === 'employee' ? 'Employee' : (log.done_by_role || 'Admin')}
                              size="small"
                              sx={{
                                background: log.actor_type === 'employee' ? '#e8f5e9' : '#e8eaf6',
                                color:      log.actor_type === 'employee' ? '#2e7d32' : '#1a237e',
                                fontWeight: 600, fontSize: 10,
                              }}
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div" count={filtered.length} page={page}
              onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage}
              onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              sx={{ borderTop: '1px solid #f0f0f0', color: '#1a237e' }}
            />
          </>
        )}
      </Card>
    </Box>
  );
}
